import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../shared/Icon';
import { plural, scenarioUsage } from '../../../../models/LibraryModel';
import { useScenariosViewModel } from '../../../../viewModels/useScenariosViewModel';
import { useLibraryPageEnabled } from '../../../../utils/useLibraryPageEnabled';
import ArchiveConfirmDialog from '../../Library/ArchiveConfirmDialog';
import ScenarioDialog from './ScenarioDialog';
import ScenariosTable from './ScenariosTable';
import ScenariosFilters from './ScenariosFilters';
import ScenariosEmpty from './ScenariosEmpty';
import '../../Library/Library.css'; // the Library's search, select, count line and empty state
import '../NavySettings.css'; // navy-settings-head
import './Scenarios.css';

/**
 * Settings > Assessments (PRD-1701, Navy Phase 0 handoff "Scenarios"): the
 * assessment scenarios, each a saved selection of Library documents. The
 * search and visibility filter sit in the panel above the table, with the
 * count line under them; the filter opens on All. With no scenarios at all the
 * panel holds the empty state instead. Scenarios are archived, never deleted:
 * Archive asks first, Unarchive doesn't.
 */
function ScenariosSection({ model, onShowNotification, addOnOpen = false, onAddOpened, returnToAdd = false, onReturnToAdd }) {
  const vm = useScenariosViewModel(model);
  const navigate = useNavigate();
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const addRef = useRef(null);
  const [editing, setEditing] = useState(null); // { scenario, opener } while the dialog is open
  const [confirming, setConfirming] = useState(null); // { scenario, opener } while archive asks
  const [refocusId, setRefocusId] = useState(null); // the scenario whose trigger takes focus back
  const libraryPageEnabled = useLibraryPageEnabled();
  const { filters, visibleScenarios, scopeTotal, documents } = vm;
  const count = visibleScenarios.length;

  // The menu's trigger, so focus goes back to it when a dialog closes.
  const menuTrigger = (scenario) => rootRef.current?.querySelector(`[data-scenario-menu="${scenario.id}"] button`) || null;

  // Unarchive happens in place, and the menu that ran it is gone, so focus goes
  // back to the row's trigger once it has re-rendered, or to Add scenario when
  // the row has left the view (the Archived filter).
  useEffect(() => {
    if (!refocusId) return;
    const trigger = rootRef.current?.querySelector(`[data-scenario-menu="${refocusId}"] button`);
    (trigger || addRef.current)?.focus();
    setRefocusId(null);
  }, [refocusId]);

  // Add assessment's "Add scenario" lands here with the dialog already open.
  useEffect(() => {
    if (!addOnOpen) return;
    setEditing({ scenario: null, opener: addRef.current });
    onAddOpened?.();
  }, [addOnOpen, onAddOpened]);

  const handleClear = () => {
    vm.clearFilters();
    searchRef.current?.focus();
  };

  const handleSave = (fields) => {
    const saved = vm.saveScenario(fields);
    if (!saved) return;
    setEditing(null);
    onShowNotification?.(fields.id ? 'Scenario saved.' : 'Scenario added.', 'success');
  };

  const menuActions = {
    onEdit: (scenario) => setEditing({ scenario, opener: menuTrigger(scenario) }),
    onArchive: (scenario) => {
      const used = scenarioUsage(scenario);
      if (used) {
        onShowNotification?.(`${used} ${plural(used, 'assessment uses', 'assessments use')} this scenario.`, 'alert');
        return;
      }
      setConfirming({ scenario, opener: menuTrigger(scenario) });
    },
    onUnarchive: (scenario) => {
      if (!vm.unarchive(scenario.id)) return;
      setRefocusId(scenario.id);
      onShowNotification?.('Scenario unarchived.', 'success');
    },
  };

  const confirmArchive = () => {
    const { scenario } = confirming;
    setConfirming(null);
    if (vm.archive(scenario.id)) onShowNotification?.('Scenario archived.', 'success');
  };

  const closeEditing = useCallback(() => setEditing(null), []);

  // Nothing rather than a false "No scenarios yet" before they arrive.
  if (!vm.ready) return null;

  return (
    <div className="scenarios" ref={rootRef}>
      <div className="navy-settings-head">
        <h2 className="navy-settings-title">Assessment scenarios</h2>
        <button
          ref={addRef}
          type="button"
          className="resui-btn resui-btn--primary"
          onClick={(e) => setEditing({ scenario: null, opener: e.currentTarget })}
        >
          Add scenario
        </button>
      </div>

      <div className="library-panel scenarios-panel">
        <p className="scenarios-intro">
          A scenario is one or more reference keys from the library, with a narrative and SOPs if you have them.
          Selecting a scenario is required when an assessment is created.
        </p>

        {/* PRD-1703: the way back to Add assessment, whose draft was kept. */}
        {returnToAdd && (
          <div className="resui-banner resui-banner--info scenarios-note">
            <Icon name="arrow-left" size={18} />
            <span className="resui-banner-text">
              You came here from Add assessment.{' '}
              <button type="button" className="resui-link" onClick={onReturnToAdd}>Return to add assessment</button>
            </span>
          </div>
        )}

        <div className="resui-banner resui-banner--info scenarios-note">
          <Icon name="info" size={18} />
          <span className="resui-banner-text">
            Reference keys, SOPs and narratives are uploaded in the{' '}
            {/* A link only while the Library page is open to follow it to. */}
            {libraryPageEnabled
              ? <button type="button" className="resui-link" onClick={() => navigate('/library')}>library</button>
              : 'library'}.
          </span>
        </div>

        {vm.scenarios.length === 0 ? (
          <ScenariosEmpty onAdd={(e) => setEditing({ scenario: null, opener: e.currentTarget })} />
        ) : (
          <>
            <ScenariosFilters
              filters={filters}
              count={count}
              total={scopeTotal}
              searchRef={searchRef}
              onFilterChange={vm.setFilter}
              onClear={handleClear}
            />

            <ScenariosTable
              scenarios={visibleScenarios}
              documents={documents}
              sort={vm.sort}
              onSortChange={vm.setSort}
              resetKey={`${filters.search}|${filters.visibility}`}
              menuActions={menuActions}
            />
          </>
        )}
      </div>

      {editing && (
        <ScenarioDialog
          scenario={editing.scenario}
          documents={documents}
          opener={editing.opener}
          fallbackFocus={addRef}
          onSave={handleSave}
          onClose={closeEditing}
        />
      )}

      {confirming && (
        <ArchiveConfirmDialog
          item={confirming.scenario}
          kind="scenario"
          opener={confirming.opener}
          fallbackFocus={addRef}
          onConfirm={confirmArchive}
          onClose={() => setConfirming(null)}
        />
      )}
    </div>
  );
}

export default ScenariosSection;
